import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardPathFromToken } from "../../utils/dashboardRouting";
import { clearContextStorage, getStoredContext, saveContext } from "../../utils/contextStorage";

const BASE_URL = "https://mis.empowerplus.in";
const ALL_VALUE = "__ALL__";

const FY_OPTIONS = [
    { value: "23-24", label: "2023-24" },
    { value: "24-25", label: "2024-25" },
    { value: "25-26", label: "2025-26" },
    { value: "26-27", label: "2026-27" }
];

const normalizeValue = (value) => value === ALL_VALUE ? "" : value;

const parseDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getFinancialYearRange = (fy) => {
    const [startYear, endYear] = String(fy || "").split("-");

    if (!startYear || !endYear) {
        return null;
    }

    const fyStart = new Date(Number(`20${startYear}`), 3, 1);
    const fyEnd = new Date(Number(`20${endYear}`), 2, 31, 23, 59, 59, 999);

    return { fyStart, fyEnd };
};

const matchesFinancialYear = (project, fy) => {
    if (!fy) return true;

    const range = getFinancialYearRange(fy);

    if (!range) {
        return true;
    }

    const projectStart = parseDate(project?.projectStartDate) || parseDate(project?.agreementDate) || parseDate(project?.createdDate);
    const projectEnd = parseDate(project?.projectEndDate) || projectStart;

    if (projectStart && projectEnd) {
        return projectStart <= range.fyEnd && projectEnd >= range.fyStart;
    }

    return true;
};

const matchesDepartment = (project, department) => {
    if (!department) return true;

    return String(project?.department || project?.departmentCode || "")
        .trim()
        .toUpperCase() === String(department).trim().toUpperCase();
};

const getProjectCenters = (center) => [
    center?.projectId,
    center?.project?.projectId,
    center?.projectName,
    center?.project?.projectName
].filter(Boolean);

export default function AdminChooseContext() {
    const token = localStorage.getItem("token");
    const navigate = useNavigate();
    const [msg, setMsg] = useState({ type: "", text: "" });
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [projects, setProjects] = useState([]);
    const [centers, setCenters] = useState([]);
    const [fy, setFy] = useState(ALL_VALUE);
    const [department, setDepartment] = useState(ALL_VALUE);
    const [project, setProject] = useState(ALL_VALUE);
    const [centerId, setCenterId] = useState(ALL_VALUE);

    const authHeaders = useMemo(() => ({
        Authorization: `Bearer ${token}`
    }), [token]);

    const flash = (type, text) => {
        setMsg({ type, text });
        window.setTimeout(() => setMsg({ type: "", text: "" }), 2500);
    };

    const navigateToDashboard = useCallback(() => {
        if (!token) {
            navigate("/login");
            return;
        }

        try {
            navigate(getDashboardPathFromToken(token));
        } catch {
            localStorage.removeItem("token");
            navigate("/login");
        }
    }, [navigate, token]);

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : [];
        } catch {
            return [];
        }
    };

    const loadOptions = useCallback(async () => {
        setLoading(true);

        try {
            const [departmentRes, projectRes, centerRes] = await Promise.all([
                fetch(`${BASE_URL}/api/departments`, { headers: authHeaders }),
                fetch(`${BASE_URL}/api/projects`, { headers: authHeaders }),
                fetch(`${BASE_URL}/api/centers`, { headers: authHeaders })
            ]);

            const [departmentData, projectData, centerData] = await Promise.all([
                safeJson(departmentRes),
                safeJson(projectRes),
                safeJson(centerRes)
            ]);

            setDepartments(Array.isArray(departmentData) ? departmentData : []);
            setProjects(Array.isArray(projectData) ? projectData : projectData?.content || []);
            setCenters(Array.isArray(centerData) ? centerData : centerData?.content || []);
        } catch {
            setDepartments([]);
            setProjects([]);
            setCenters([]);
        } finally {
            setLoading(false);
        }
    }, [authHeaders]);

    useEffect(() => {
        loadOptions();
    }, [loadOptions]);

    useEffect(() => {
        const stored = getStoredContext();

        setFy(stored?.fy || ALL_VALUE);
        setDepartment(stored?.department || ALL_VALUE);
        setProject(stored?.project || ALL_VALUE);
        setCenterId(stored?.centerId || ALL_VALUE);
    }, []);

    const filteredProjects = useMemo(() => {
        return projects.filter((item) => (
            matchesFinancialYear(item, normalizeValue(fy)) &&
            matchesDepartment(item, normalizeValue(department))
        ));
    }, [department, fy, projects]);

    const filteredDepartments = useMemo(() => {
        const rows = projects.filter((item) => matchesFinancialYear(item, normalizeValue(fy)));
        const seen = new Map();

        rows.forEach((item) => {
            const departmentCode = String(item?.department || "").trim();

            if (!departmentCode || seen.has(departmentCode)) {
                return;
            }

            const matchedDepartment = departments.find((entry) =>
                String(entry?.departmentCode || "").trim().toUpperCase() === departmentCode.toUpperCase()
            );

            seen.set(departmentCode, {
                value: departmentCode,
                label: matchedDepartment?.departmentName || departmentCode
            });
        });

        return [...seen.values()];
    }, [departments, fy, projects]);

    const filteredCenters = useMemo(() => {
        const selectedProjectId = normalizeValue(project);

        if (!selectedProjectId) {
            return centers;
        }

        return centers.filter((item) =>
            getProjectCenters(item).some((value) => String(value).trim() === selectedProjectId)
        );
    }, [centers, project]);

    useEffect(() => {
        const selectedDepartment = normalizeValue(department);

        if (
            selectedDepartment &&
            filteredDepartments.length &&
            !filteredDepartments.some((item) => item.value === selectedDepartment)
        ) {
            setDepartment(ALL_VALUE);
            setProject(ALL_VALUE);
            setCenterId(ALL_VALUE);
        }
    }, [department, filteredDepartments]);

    useEffect(() => {
        const selectedProjectId = normalizeValue(project);

        if (
            selectedProjectId &&
            filteredProjects.length &&
            !filteredProjects.some((item) => item.projectId === selectedProjectId)
        ) {
            setProject(ALL_VALUE);
            setCenterId(ALL_VALUE);
        }
    }, [filteredProjects, project]);

    useEffect(() => {
        const selectedCenter = normalizeValue(centerId);

        if (
            selectedCenter &&
            filteredCenters.length &&
            !filteredCenters.some((item) => item.centerId === selectedCenter)
        ) {
            setCenterId(ALL_VALUE);
        }
    }, [centerId, filteredCenters]);

    const handleConfirm = () => {
        const selectedProject = filteredProjects.find((item) => item.projectId === normalizeValue(project));

        saveContext({
            fy: normalizeValue(fy),
            department: normalizeValue(department),
            project: normalizeValue(project),
            projectName: selectedProject?.projectName || "",
            centerId: normalizeValue(centerId)
        });

        flash("success", "Admin context updated successfully");
        navigateToDashboard();
    };

    const handleReset = () => {
        clearContextStorage();
        setFy(ALL_VALUE);
        setDepartment(ALL_VALUE);
        setProject(ALL_VALUE);
        setCenterId(ALL_VALUE);
        flash("success", "Context cleared. Showing all data.");
    };

    return (
        <>
            <style>{`
                .admin-context-page{
                    min-height:100vh;
                    display:flex;
                    justify-content:center;
                    align-items:center;
                    padding:24px;
                    background:radial-gradient(circle at top left,#dbeafe 0%,#eff6ff 35%,#fff7ed 100%);
                    font-family:Segoe UI,system-ui,sans-serif;
                }
                .admin-context-card{
                    width:min(980px,100%);
                    background:#fff;
                    border-radius:22px;
                    padding:40px;
                    box-shadow:0 24px 80px rgba(15,23,42,.12);
                    border:1px solid rgba(148,163,184,.18);
                }
                .admin-context-grid{
                    display:grid;
                    grid-template-columns:repeat(auto-fit,minmax(190px,1fr));
                    gap:18px;
                    margin-top:28px;
                }
                .admin-context-field label{
                    display:block;
                    margin-bottom:8px;
                    font-size:14px;
                    font-weight:700;
                    color:#0f172a;
                }
                .admin-context-field select{
                    width:100%;
                    padding:12px 14px;
                    border-radius:12px;
                    border:1px solid #cbd5e1;
                    outline:none;
                    font-size:14px;
                    background:#fff;
                }
                .admin-context-field select:focus{
                    border-color:#2563eb;
                    box-shadow:0 0 0 4px rgba(37,99,235,.12);
                }
                .admin-context-actions{
                    display:flex;
                    gap:12px;
                    flex-wrap:wrap;
                    margin-top:28px;
                }
                .admin-context-actions button{
                    border:none;
                    border-radius:12px;
                    padding:12px 22px;
                    font-size:14px;
                    font-weight:800;
                    cursor:pointer;
                }
                .admin-context-primary{background:#1d4ed8;color:#fff;}
                .admin-context-secondary{background:#e2e8f0;color:#0f172a;}
                .admin-context-reset{background:#dc2626;color:#fff;}
                .admin-context-note{
                    margin-top:14px;
                    font-size:13px;
                    color:#64748b;
                    line-height:1.5;
                }
                .admin-context-msg{
                    margin-top:14px;
                    font-size:14px;
                    font-weight:700;
                }
                .admin-context-msg.success{color:#15803d;}
                .admin-context-msg.error{color:#b91c1c;}
            `}</style>

            <div className="admin-context-page">
                <div className="admin-context-card">
                    <div>
                        <div style={{ display: "inline-block", padding: "6px 12px", borderRadius: 999, background: "#dbeafe", color: "#1d4ed8", fontWeight: 800, fontSize: 12, textTransform: "uppercase", letterSpacing: ".08em" }}>
                            Admin Context
                        </div>
                        <h2 style={{ margin: "16px 0 8px", fontSize: 34, fontWeight: 900, color: "#0f172a" }}>Filter The Admin View</h2>
                        <p style={{ margin: 0, color: "#64748b", fontSize: 15 }}>
                            Choose specific FY, department, project, and center, or keep any dropdown on All Data to see everything.
                        </p>
                    </div>

                    <div className="admin-context-grid">
                        <div className="admin-context-field">
                            <label>Financial Year</label>
                            <select
                                value={fy}
                                onChange={(e) => {
                                    setFy(e.target.value);
                                    setProject(ALL_VALUE);
                                    setCenterId(ALL_VALUE);
                                }}
                            >
                                <option value={ALL_VALUE}>All Data</option>
                                {FY_OPTIONS.map((item) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="admin-context-field">
                            <label>Department</label>
                            <select
                                value={department}
                                onChange={(e) => {
                                    setDepartment(e.target.value);
                                    setProject(ALL_VALUE);
                                    setCenterId(ALL_VALUE);
                                }}
                                disabled={loading}
                            >
                                <option value={ALL_VALUE}>All Data</option>
                                {filteredDepartments.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="admin-context-field">
                            <label>Project</label>
                            <select
                                value={project}
                                onChange={(e) => {
                                    setProject(e.target.value);
                                    setCenterId(ALL_VALUE);
                                }}
                                disabled={loading}
                            >
                                <option value={ALL_VALUE}>All Data</option>
                                {filteredProjects.map((item) => (
                                    <option key={item.projectId} value={item.projectId}>
                                        {item.projectName || item.projectId}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="admin-context-field">
                            <label>Center</label>
                            <select
                                value={centerId}
                                onChange={(e) => setCenterId(e.target.value)}
                                disabled={loading}
                            >
                                <option value={ALL_VALUE}>All Data</option>
                                {filteredCenters.map((item) => (
                                    <option key={item.centerId} value={item.centerId}>
                                        {item.trainingCentreName || item.centerName || item.centerId}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="admin-context-actions">
                        <button className="admin-context-primary" onClick={handleConfirm}>Apply Context</button>
                        <button className="admin-context-secondary" onClick={navigateToDashboard}>Back To Dashboard</button>
                        <button className="admin-context-reset" onClick={handleReset}>Reset To All Data</button>
                    </div>

                    <div className="admin-context-note">
                        Center-based APIs will be used when a center is selected. Center and project APIs will be used together when both center and project are selected.
                    </div>

                    {msg.text ? <div className={`admin-context-msg ${msg.type}`}>{msg.text}</div> : null}
                </div>
            </div>
        </>
    );
}
